import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/components/Toaster";
import { Problem } from "@/components/PreviewBits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { combinationProblem, flagsBody, PRESETS, switchChanges, switchName, type SiteRow, type SwitchDef } from "./siteSwitches";

/** "Apply a set of switches" button for Settings → Websites. */
export function SiteSwitchSetButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Apply a set of switches
      </Button>
      {open && <SiteSwitchSetDialog onClose={() => setOpen(false)} />}
    </>
  );
}

/** Several switches on one site in one change (sites_flags_set), with a preview of every change before it is made. */
export function SiteSwitchSetDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ["sites"], queryFn: () => api<{ sites: SiteRow[]; switches: SwitchDef[] }>("/api/v1/sites") });
  const [siteKey, setSiteKey] = useState("");
  const [wanted, setWanted] = useState<Record<string, string>>({});
  const [review, setReview] = useState(false);
  const [problem, setProblem] = useState("");
  const sites = q.data?.sites ?? [];
  const defs = q.data?.switches ?? [];
  const site = sites.find((s) => s.site === siteKey);
  const siteDefs = site ? defs.filter((d) => d.sites.includes(site.site)) : [];
  const changes = site ? switchChanges(site, defs, wanted) : [];
  const combo = site ? combinationProblem(site, defs, wanted) : "";
  const presets = PRESETS.filter((p) => p.site === siteKey);
  const save = useMutation({
    mutationFn: () => api<{ changed: string[] }>(`/api/v1/sites/${siteKey}/flags`, { json: { flags: flagsBody(defs, changes) } }),
    onMutate: () => setProblem(""),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["sites"] });
      toast(`${site?.name}: ${r.changed.join(", ") || "no change"}`);
      onClose();
    },
    onError: (e) => {
      setProblem(e.message);
      setReview(false);
    },
  });
  const chooseSite = (k: string) => {
    setSiteKey(k);
    setWanted({});
    setProblem("");
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(640px,calc(100vw-2rem))]">
        <DialogTitle>{review ? `Check before changing ${site?.name}` : "Apply a set of switches"}</DialogTitle>
        {!review ? (
          <DialogDescription>
            Change several switches on one site at once, so members never see it half switched. You see every change before anything happens; the site picks it up within about a
            minute.
          </DialogDescription>
        ) : (
          <DialogDescription>
            Everyone using <span className="font-medium">{site?.base_url || site?.name}</span> gets these within about a minute. You can switch them back the same way.
          </DialogDescription>
        )}
        {q.isLoading && <Skeleton className="h-32" />}
        {q.error && <Problem text={`Couldn't load the sites: ${q.error.message}`} />}
        {q.data && !review && (
          <div className="space-y-3 text-sm">
            <div className="space-y-1">
              <Label htmlFor="ss-site">Site</Label>
              <Select id="ss-site" value={siteKey} onChange={(e) => chooseSite(e.target.value)}>
                <option value="">Choose…</option>
                {sites.map((s) => (
                  <option key={s.site} value={s.site}>
                    {s.name} ({s.site})
                  </option>
                ))}
              </Select>
            </div>
            {presets.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => (
                  <Button key={p.label} size="sm" variant="outline" onClick={() => setWanted({ ...p.flags })} title={p.step}>
                    {p.label}
                  </Button>
                ))}
              </div>
            )}
            {site && (
              <div className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
                {siteDefs.map((d) => {
                  const cur = site.flags[d.name] ?? d.default;
                  return (
                    <div key={d.name} className="grid gap-1 sm:grid-cols-[minmax(0,1fr)_11rem] sm:items-center">
                      <label htmlFor={`ss-${d.name}`}>
                        <span className="font-medium">{switchName(d.name)}</span>
                        <span className="block text-xs text-[var(--color-muted-foreground)]">
                          now {cur}
                          {cur === d.default ? " (Keap, the original)" : ""}
                        </span>
                      </label>
                      <Select
                        id={`ss-${d.name}`}
                        value={wanted[d.name] ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setWanted((w) => {
                            const n = { ...w };
                            if (v) n[d.name] = v;
                            else delete n[d.name];
                            return n;
                          });
                        }}
                      >
                        <option value="">Leave as is</option>
                        {d.values.map((v) => (
                          <option key={v} value={v}>
                            {v}
                            {v === d.default ? " (Keap, the original)" : ""}
                          </option>
                        ))}
                      </Select>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {review && site && (
          <div className="space-y-2 text-sm">
            <ul className="space-y-1">
              {changes.map(([n, from, to]) => (
                <li key={n} className="flex flex-wrap gap-x-2">
                  <span className="font-medium">{switchName(n)}:</span>
                  <span>
                    {from} to <span className="font-semibold">{to}</span>
                  </span>
                </li>
              ))}
            </ul>
            <p className="flex items-start gap-2 rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-xs text-[var(--color-warning-fg)]">
              <ShieldAlert className="mt-0.5 size-3.5 shrink-0" /> This changes a live website. Check a member can still sign in right after (a test login, or "Why can't they
              sign in?" on their contact).
            </p>
          </div>
        )}
        {!review && site && combo && changes.length > 0 && <Problem text={`This set would be refused: ${combo}`} />}
        <Problem text={problem} />
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          {!review && site && <span className="mr-auto text-xs text-[var(--color-muted-foreground)]">{changes.length === 0 ? "Nothing to change yet." : `${changes.length} to change`}</span>}
          <Button variant="outline" onClick={review ? () => setReview(false) : onClose}>
            {review ? "Back" : "Cancel"}
          </Button>
          {!review ? (
            <Button disabled={!site || changes.length === 0 || !!combo} onClick={() => setReview(true)}>
              Review
            </Button>
          ) : (
            <Button variant="destructive" disabled={save.isPending} onClick={() => save.mutate()}>
              Change {changes.length} switch{changes.length === 1 ? "" : "es"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
